import React, { useContext, useEffect, useMemo } from "react";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import "../css/cartpage.scss";
import { Link, useNavigate } from "react-router-dom";
import siteConfig, { formatPrice } from "../config/siteConfig";

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, clearCart } = useContext(CartContext);

  useEffect(() => {
    document.title = `Shopping bag — ${siteConfig.brandName}`;
  }, []);

  const detailedItems = useMemo(
    () =>
      cartItems
        .map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = detailedItems.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );
  const shipping = subtotal >= siteConfig.shipping.freeThreshold ? 0 : siteConfig.shipping.standard;
  const amountToFreeShipping = Math.max(siteConfig.shipping.freeThreshold - subtotal, 0);

  if (detailedItems.length === 0) {
    return (
      <main className="cart-page cart-page--empty">
        <div>
          <p className="cart-page__eyebrow">Your bag</p>
          <h1>Your cart is empty</h1>
          <p>Discover considered denim and the latest seasonal silhouettes.</p>
          <Link to="/shop">Continue shopping</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <div className="cart-page__heading">
        <p className="cart-page__eyebrow">Shopping bag</p>
        <h1>Your selection</h1>
        <p>{detailedItems.length} {detailedItems.length === 1 ? "piece" : "pieces"}</p>
      </div>

      <div className="cart-progress">
        <div style={{ width: `${Math.min((subtotal / siteConfig.shipping.freeThreshold) * 100, 100)}%` }} />
        <p>
          {amountToFreeShipping > 0
            ? `Add ${formatPrice(amountToFreeShipping)} for complimentary delivery.`
            : "Your order qualifies for complimentary delivery."}
        </p>
      </div>

      <div className="cart-page__layout">
        <section className="cart-items" aria-label="Cart items">
          {detailedItems.map((item) => (
            <article className="cart-item" key={`${item.id}-${item.size}-${item.color}`}>
              <Link className="cart-item__image" to={`/shopDetails/${item.id}`}>
                <img src={item.product.image} alt={item.product.name} />
              </Link>
              <div className="cart-item__info">
                <p className="cart-item__collection">{item.product.collection}</p>
                <Link to={`/shopDetails/${item.id}`}>{item.product.name}</Link>
                <dl>
                  <div><dt>Color</dt><dd>{item.color}</dd></div>
                  <div><dt>Size</dt><dd>{item.size}</dd></div>
                </dl>
                <div className="cart-item__quantity" aria-label={`Quantity for ${item.product.name}`}>
                  <button
                    type="button"
                    onClick={() => updateQuantity(item.id, item.size, item.color, Math.max(1, item.quantity - 1))}
                    aria-label="Decrease quantity"
                  >−</button>
                  <span>{item.quantity}</span>
                  <button
                    type="button"
                    onClick={() => updateQuantity(item.id, item.size, item.color, Math.min(10, item.quantity + 1))}
                    aria-label="Increase quantity"
                  >＋</button>
                </div>
                <button
                  type="button"
                  className="cart-item__remove"
                  onClick={() => removeFromCart(item.id, item.size, item.color)}
                >Remove</button>
              </div>
              <p className="cart-item__price">{formatPrice(item.product.price * item.quantity)}</p>
            </article>
          ))}
          <div className="cart-items__actions">
            <Link to="/shop">Continue shopping</Link>
            <button type="button" onClick={clearCart}>Clear bag</button>
          </div>
        </section>

        <aside className="cart-summary">
          <h2>Order summary</h2>
          <dl>
            <div><dt>Subtotal</dt><dd>{formatPrice(subtotal)}</dd></div>
            <div><dt>Delivery</dt><dd>{shipping === 0 ? "Complimentary" : formatPrice(shipping)}</dd></div>
          </dl>
          <div className="cart-summary__total">
            <span>
              Total
              <small>Taxes included</small>
            </span>
            <strong>{formatPrice(subtotal + shipping)}</strong>
          </div>
          <button type="button" onClick={() => navigate("/checkout")}>Checkout securely</button>
          <p>Secure checkout · 14-day returns · Tracked delivery</p>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
